import api from './api';

export interface RemitoCompraDetalle {
  nroIdformulario: number;
  nroItem: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad?: number;
  precioUnitario?: number;
  descuento?: number;
  subtotal?: number;
  notas?: string;
}

export interface RemitoCompra {
  nroFormulario: number;
  nroRemito?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string | Date;
  horaEmision?: string | Date;
  nroTipoFormulario?: string;
  tipoCliente?: string;

  // A nivel de modelo se usa NroCliente, pero representa al proveedor en remitos de compra.
  nroCliente?: number;
  nombre?: string;
  nroLista?: string;
  codigoMoneda?: number;
  importeTotal?: number;
  asentado?: string;
  descripcion?: string;
  usuario?: string;

  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;

  nroUbicacionOrigen?: number;
  detalles?: RemitoCompraDetalle[];
}

export interface RemitoCompraSearchParams {
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  fechaDesde?: Date | string;
  fechaHasta?: Date | string;
  nroProveedor?: string;
  asentado?: string;
  nroSuc?: number;
}

export interface RemitoCompraPagedResult {
  items: RemitoCompra[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface RemitoCompraFormularioDto {
  descripcion?: string;
  nroSuc?: number;
  fechaEmision?: string | Date;
  horaEmision?: string | Date;
  fechaVencim?: string | Date;

  // Remito compra: requerido (proveedor)
  nroProveedor: number;

  nombre?: string;
  cuit?: string;
  nroLista?: string;

  // Depósito/ubicación origen del stock
  nroUbicacionOrigen?: number;

  direccionEnvio?: string;
  codigoCiudadEnvio?: number;
  formaEntrega?: string;

  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;
}

export interface RemitoCompraCreateItemDto {
  codigoArticulo: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc: number;
  notas?: string;

  // Campos opcionales, soportados por el backend
  um?: string;
  codigoMoneda?: number;
  cotizacion?: number;
}

export interface RemitoCompraCreatePayload {
  formulario: RemitoCompraFormularioDto;
  items: RemitoCompraCreateItemDto[];
}

export interface AnularRemitoCompraDto {
  motivo?: string;
}

const remitoCompraService = {
  getRemitosCompra: async (params: RemitoCompraSearchParams): Promise<RemitoCompraPagedResult> => {
    const queryParams: Record<string, unknown> = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
      SearchTerm: params.searchTerm || undefined,
      NroProveedor: params.nroProveedor || undefined,
      Asentado: params.asentado || undefined,
      NroSuc: params.nroSuc ?? undefined,
    };

    if (params.fechaDesde) {
      queryParams.FechaDesde =
        typeof params.fechaDesde === 'string' ? params.fechaDesde : params.fechaDesde.toISOString().split('T')[0];
    }
    if (params.fechaHasta) {
      queryParams.FechaHasta =
        typeof params.fechaHasta === 'string' ? params.fechaHasta : params.fechaHasta.toISOString().split('T')[0];
    }

    const response = await api.get<RemitoCompraPagedResult>('/api/RemitosCompra', { params: queryParams });
    return response.data;
  },

  getRemitoCompra: async (id: number): Promise<RemitoCompra> => {
    const response = await api.get<RemitoCompra>(`/api/RemitosCompra/${id}`);
    return response.data;
  },

  createRemitoCompra: async (payload: RemitoCompraCreatePayload): Promise<RemitoCompra> => {
    const response = await api.post<RemitoCompra>('/api/RemitosCompra', payload);
    return response.data;
  },

  updateRemitoCompra: async (id: number, payload: RemitoCompraCreatePayload): Promise<void> => {
    await api.put(`/api/RemitosCompra/${id}`, payload);
  },

  anularRemitoCompra: async (id: number, dto?: AnularRemitoCompraDto): Promise<void> => {
    await api.post(`/api/RemitosCompra/${id}/anular`, dto ?? {});
  },
};

export default remitoCompraService;

